import { Controller } from "stimulus";
import AreaCookieController from "./area_cookie_controller";

export default class extends Controller {
  static targets = ["container"];
  static values = {
    currentCompanyId: Number,
    apiUrl: String
  };

  connect() {
    this.loadAreaRecommendations();
  }

  async loadAreaRecommendations() {
    const areaData = AreaCookieController.getLastViewedArea();

    if (!areaData) {
      return;
    }

    try {
      const response = await this.fetchRecommendations(areaData);
      if (response.ok) {
        const data = await response.json();
        this.updateRecommendationsDisplay(data);
      } else {
        console.warn(`API呼び出しエラー: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.warn('レコメンド情報の取得に失敗しました:', error);
    }
  }

  async fetchRecommendations(areaData) {
    const params = new URLSearchParams({
      current_company_id: this.currentCompanyIdValue,
      prefecture_id: areaData.prefecture_id
    });

    if (areaData.city_id) {
      params.append('city_id', areaData.city_id);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(`${this.apiUrlValue}?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  updateRecommendationsDisplay(data) {
    if (!this.containerTarget || !data.companies || data.companies.length === 0) {
      return;
    }

    const html = this.buildRecommendationsHTML(data.companies);
    this.containerTarget.innerHTML = html;
  }

  buildRecommendationsHTML(companies) {
    const companiesHTML = companies.map(company => this.buildCompanyCardHTML(company)).join('');

    // PC版の場合はタイトルとコンテンツを含む、SP版の場合はカードのみ
    if (this.containerTarget.classList.contains('other-companies__container')) {
      // PC版
      return `
        <div class="other-companies__title">あなたのエリアでおすすめの会社</div>
        <div class="other-companies__content">
          ${companiesHTML}
        </div>
      `;
    } else {
      // SP版
      return companiesHTML;
    }
  }

  buildCompanyCardHTML(company) {
    const headerSubText = company.header_sub_text ? `
      <div class="small-company-card__company-sub-text">${this.escapeHtml(company.header_sub_text)}</div>
    ` : '';

    const imageHTML = company.image_url ? `
      <a href="/company/${(company.id)}" class="small-company-card__image js-ga-measure_target">
        <img src="${this.escapeHtml(company.image_url)}" alt="${this.escapeHtml(company.name)}" loading="lazy">
      </a>
    ` : `
      <a href="/company/${(company.id)}" class="small-company-card__image small-company-card__image--empty js-ga-measure_target">
        <svg xmlns="http://www.w3.org/2000/svg" width="37" height="42" viewBox="0 0 59.398 64.312" alt="${this.escapeHtml(company.name)}">
          <path d="M72.208,11.992H43.232L39.206,2.516a4.128,4.128,0,1,0-7.6,3.227l2.654,6.249H32.985a6.764,6.764,0,0,0-6.757,6.749A6.686,6.686,0,0,0,28.3,23.618l.284,3.408.144,1.749-.1-.118a35.793,35.793,0,0,0-6.441,7.415,20.052,20.052,0,0,0-1.9,3.773,10.97,10.97,0,0,0-.73,3.723,6.379,6.379,0,0,0,1.147,3.781,6.7,6.7,0,0,0,3.443,2.393,5.253,5.253,0,0,0,1.641.245,11.067,11.067,0,0,0,4.122-.981,43.449,43.449,0,0,0,8.119-4.783A117.449,117.449,0,0,0,50.447,33.818c.4-.386.787-.763,1.166-1.139a4.152,4.152,0,0,1-2.156-3.622,118.069,118.069,0,0,1-11.3,9.981,61.9,61.9,0,0,1-7.4,5.01,21.294,21.294,0,0,1-3,1.412,6.184,6.184,0,0,1-1.971.443,1.16,1.16,0,0,1-.371-.044,2.627,2.627,0,0,1-1.4-.9,2.3,2.3,0,0,1-.377-1.392,6.953,6.953,0,0,1,.482-2.319,18.715,18.715,0,0,1,2.641-4.62,31.66,31.66,0,0,1,2.384-2.79l.183,2.218.555,6.6.074-.04c1.062-.588,2.239-1.324,3.505-2.19l-.662-8-.139-1.659-.625-7.586H51.09v5.869a2.536,2.536,0,0,0,5.072,0V27.339a1.741,1.741,0,1,1,3.483,0v6.985a3.486,3.486,0,0,0,6.972,0V23.183h6.791l-2.565,31a6.951,6.951,0,0,1-6.93,6.374H41.532a6.942,6.942,0,0,1-6.921-6.374l-.32-3.825L34.153,48.7a30.567,30.567,0,0,1-3.619,1.822l.017.147.311,3.824a10.771,10.771,0,0,0,10.671,9.822H63.914A10.761,10.761,0,0,0,74.586,54.49l2.582-31.173a6.749,6.749,0,0,0-4.96-11.325ZM51.09,16.09v5.133H31.322a2.985,2.985,0,0,1,1.663-5.473H51.124A2.355,2.355,0,0,0,51.09,16.09Zm22.772,5.133H66.617V16.09a2.119,2.119,0,0,0-.034-.34h5.625a2.984,2.984,0,0,1,1.654,5.473Z" transform="translate(-19.559 0)" fill="#d6d6d6"/>
        </svg>
      </a>
    `;

    const reviewCountHTML = company.review_count === 0 ? `
      <div class="small-company-card__review-count small-company-card__review-count--pc">
        <div class="small-company-card__review-count">
          (${company.review_count}件)
        </div>
      </div>
      <div class="small-company-card__review-count small-company-card__review-count--sp">
        <div class="small-company-card__review-count">
          (${company.review_count}件)
        </div>
      </div>
    ` : `
      <div class="small-company-card__review-count small-company-card__review-count--pc">
        <div class="small-company-card__review-count">
          (<a href="/company/${company.id}/review" class="small-company-card__link js-ga-measure_target">全${company.review_count}件</a>)
        </div>
      </div>
      <div class="small-company-card__review-count small-company-card__review-count--sp">
        <div class="small-company-card__review-count">
          (${company.review_count}件)
        </div>
      </div>
    `;

    const starRatingHTML = this.buildStarRatingHTML(company.rating, company.rating_displayable);

    return `
      <div class="small-company-card">
        <div class="small-company-card__inner">
          <div class="small-company-card__header">
            <a href="/company/${(company.id)}" class="small-company-card__company-name small-company-card__link js-ga-measure_target">
              ${this.escapeHtml(company.name)}
            </a>
            ${headerSubText}
          </div>
          <div class="small-company-card__contents">
            ${imageHTML}
            <div class="small-company-card__info">
              <div class="small-company-card__review">
                <div class="small-company-card__rating-text">口コミ評価</div>
                ${reviewCountHTML}
              </div>
              <div class="small-company-card__rating">
                ${starRatingHTML}
              </div>
              <div class="small-company-card__contract-count small-company-card__contract-count--pc">累計工事件数：<span class="small-company-card__link"><a href="/company/${(company.id)}" class="js-ga-measure_target">${company.contract_count}</a></span>件</div>
              <div class="small-company-card__contract-count small-company-card__contract-count--sp">累計工事件数：<span class="small-company-card__link">${company.contract_count}件</span></div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  buildStarRatingHTML(rating, isRatingDisplayable = true) {
    const rate = isRatingDisplayable ? (rating || 0) : 0;
    let starsHTML = '';

    for (let index = 1; index <= 5; index++) {
      if (index === Math.ceil(rate) && (rate % 1) > 0) {
        // Half star
        starsHTML += `
          <div class="star-rating__icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="23.996" height="24.001" viewBox="0 0 23.996 24.001">
              <g transform="translate(8148.999 853)">
                <path d="M5.879,27a2.026,2.026,0,0,1-1.232-.417,2.213,2.213,0,0,1-.834-2.137l.8-4.839a2.243,2.243,0,0,0-.6-1.933L.633,14.247A2.264,2.264,0,0,1,.1,12.009a2.112,2.112,0,0,1,1.692-1.488l4.663-.706A2.1,2.1,0,0,0,8.035,8.621l2.085-4.4A2.075,2.075,0,0,1,12,3V24.214h-.006a2.024,2.024,0,0,0-.969.25L6.854,26.75A2.04,2.04,0,0,1,5.879,27Z" transform="translate(-8149 -856)" class="star-rating__half-icon--fill"/>
                <path d="M6.122,27a2.026,2.026,0,0,0,1.232-.417,2.213,2.213,0,0,0,.834-2.137l-.8-4.839a2.243,2.243,0,0,1,.6-1.933l3.375-3.427a2.264,2.264,0,0,0,.53-2.238,2.112,2.112,0,0,0-1.692-1.488L5.544,9.815A2.1,2.1,0,0,1,3.966,8.621l-2.085-4.4A2.075,2.075,0,0,0,0,3V24.214H.008a2.024,2.024,0,0,1,.969.25l4.17,2.285A2.04,2.04,0,0,0,6.122,27Z" transform="translate(-8137.002 -856)"/>
              </g>
            </svg>
          </div>
        `;
      } else {
        // Full or empty star
        const fillClass = index <= rate ? ' star-rating__icon--fill' : '';
        starsHTML += `
          <div class="star-rating__icon${fillClass}">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M10.121,1.217a2.059,2.059,0,0,1,3.759,0l2.085,4.4a2.1,2.1,0,0,0,1.578,1.195l4.663.706A2.123,2.123,0,0,1,23.9,9.008a2.247,2.247,0,0,1-.53,2.239l-3.374,3.427a2.244,2.244,0,0,0-.6,1.933l.8,4.839a2.229,2.229,0,0,1-.834,2.136,2.026,2.026,0,0,1-2.207.166l-4.171-2.285a2.022,2.022,0,0,0-1.95,0l-4.17,2.285a2.026,2.026,0,0,1-2.207-.166,2.227,2.227,0,0,1-.834-2.136l.8-4.839a2.244,2.244,0,0,0-.6-1.933L.633,11.247A2.248,2.248,0,0,1,.1,9.008,2.123,2.123,0,0,1,1.795,7.521l4.663-.706A2.1,2.1,0,0,0,8.035,5.621Z"/>
            </svg>
          </div>
        `;
      }
    }

    const displayRate = isRatingDisplayable ? rate : '-';

    return `
      <div class="star-rating">
        <div class="star-rating__icon-container">
          ${starsHTML}
        </div>
        <p class="star-rating__number">${displayRate}</p>
      </div>
    `;
  }

  escapeHtml(text) {
    if (typeof text !== 'string') {
      return '';
    }

    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
